import { ensureError } from '../guards/index.js';
import { createLogger } from '../logging/index.js';

const promiseDelay = (delay, returnValue) => new Promise((resolve) => setTimeout(() => {
  resolve(returnValue);
}, delay));
const DEFAULT_MIN_DELAY = 300;
const DEFAULT_DELAY = 3e3;
const DEFAULT_MAX_RETRIES = 5;
const promiseRetryFn = async (fn, options) => {
  const logger = createLogger(`Promise retry${options?.loggerKey ? ` (${options.loggerKey})` : ""}`, false);
  const maxRetries = options?.maxRetries ?? DEFAULT_MAX_RETRIES;
  const delay = options?.delay ?? DEFAULT_DELAY;
  const minDelay = options?.minDelay ?? DEFAULT_MIN_DELAY;
  for (let i = 0; i <= maxRetries; i += 1) {
    const attempt = i + 1;
    try {
      const res = await fn();
      logger.debug(`OK. Attempts: ${attempt.toString()}`);
      return res;
    } catch (error) {
      if (i === maxRetries) {
        logger.error(`ABORT. Attempts ${attempt.toString()}`, error);
        return Promise.reject(ensureError(error));
      }
      const calculatedDelay = Math.max(options?.delayFn?.(attempt) ?? delay, minDelay);
      logger.warn(`Fail. Attempts ${attempt.toString()}, retry in ${calculatedDelay.toString()}ms`, error);
      await promiseDelay(calculatedDelay, void 0);
    }
  }
  return Promise.reject(new Error("This should never happen, satisfying TS"));
};
const increasedDelayWithEachAttempt = (increase) => (attempt) => increase * attempt;
const promiseRetry = Object.assign(promiseRetryFn, {
  DEFAULT_DELAY,
  DEFAULT_MAX_RETRIES,
  increasedDelayWithEachAttempt
});
const sensitiveCall = (promiseFactory) => {
  return async (...args) => {
    let removeHandler = null;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    try {
      window.addEventListener("beforeunload", handleBeforeUnload);
      removeHandler = () => window.removeEventListener("beforeunload", handleBeforeUnload);
      return await promiseFactory(...args);
    } finally {
      if (removeHandler) {
        removeHandler();
      }
    }
  };
};

export { promiseDelay, promiseRetry, sensitiveCall };
//# sourceMappingURL=index.js.map
